import { MessageSquareText, Trophy, Quote, EyeOff } from "lucide-react";
import type { ComparisonResult } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Markdown } from "@/components/ui/markdown";
import { ScoreBar } from "@/components/ui/score-bar";
import { cn } from "@/lib/utils";
import { formatModelLabel } from "@/lib/format";
import { isAbsent } from "./compareResultBrief";
import { METHODOLOGY_DISCLOSURE } from "../../../methodology";

interface SentimentResultsProps {
  result: ComparisonResult;
}

export function SentimentResults({ result }: SentimentResultsProps) {
  const dimensions = ["recommendation", "sentiment", "visibility"];
  const isBlindQuery = result.query_mode === "blind_query";
  const mentions = result.mentions ?? [];

  return (
    <div className="animate-fade-up space-y-6">
      <p className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-[12px] leading-relaxed text-slate-500">
        {METHODOLOGY_DISCLOSURE}
      </p>

      {isBlindQuery && (
        <Card className="border-brand-100 bg-white">
          <CardContent className="py-4 px-6">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-8 h-8 rounded-md bg-brand-50 border border-brand-100 flex items-center justify-center">
                <EyeOff className="w-4 h-4 text-brand-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-brand-950">Blind Query</p>
                  <Badge>Blind Query</Badge>
                </div>
                <p className="mt-1 text-[12px] leading-relaxed text-slate-500">
                  No tracked brands were included in the prompt. Tracked rows below include brands that were not surfaced by the model.
                </p>
                {(result.analysis_status || result.analysis_error) && (
                  <p className="mt-2 text-[11px] text-slate-400">
                    Analysis status: {result.analysis_status || "unknown"}{result.analysis_error ? ` · ${result.analysis_error}` : ""}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Response Card — the hero of sentiment results */}
      {result.raw_response && (
        <Card className="border-brand-100 bg-brand-50/30">
          <CardContent className="py-5 px-6">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquareText className="w-4 h-4 text-brand-600" />
              <p className="text-[11px] font-semibold tracking-[0.04em] uppercase text-brand-600">
                {isBlindQuery ? "Raw Model Response" : "AI Response"}
              </p>
              <Badge variant="muted">{formatModelLabel(result.model)}</Badge>
            </div>
            <Markdown content={result.raw_response} className="text-[14px] text-brand-950" />
          </CardContent>
        </Card>
      )}

      {/* Winner + Summary */}
      {(result.winner || result.summary) && (
        <Card>
          <CardContent className="py-5 px-6">
            <div className="flex items-start gap-4">
              {result.winner && (
                <div className="shrink-0 w-10 h-10 rounded-lg bg-accent-50 border border-accent-500/20 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-accent-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {result.winner && (
                  <p className="text-sm font-semibold text-brand-950">
                    {result.winner}{" "}
                    <span className="font-normal text-slate-500">
                      was the top recommendation
                    </span>
                  </p>
                )}
                {result.summary && (
                  <p className="text-[13px] text-slate-500 mt-1 leading-relaxed">
                    {result.summary}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Brand Sentiment Cards */}
      <div className="stagger-children space-y-4">
        {result.results.map((r) => {
          const isWinner = r.brand_name === result.winner;
          const absent = isAbsent(result.query_mode, r);
          const avgScore = dimensions.length > 0 && !absent
            ? Math.round(
                dimensions.reduce((sum, d) => sum + (r.scores[d] ?? 0), 0) / dimensions.length
              )
            : 0;

          return (
            <Card
              key={r.brand_id}
              className={cn(isWinner && "border-accent-500/30 bg-accent-50/20")}
            >
              <CardContent className="py-5 px-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h4 className="text-[15px] font-semibold text-brand-950">
                      {r.brand_name}
                    </h4>
                    {isWinner && <Badge variant="winner">Recommended</Badge>}
                    {absent && <Badge variant="muted">Not surfaced</Badge>}
                  </div>
                  {absent ? (
                    <span className="text-[12px] font-semibold text-slate-400">Visibility 0</span>
                  ) : (
                    <span
                      className={cn(
                        "text-lg font-bold tabular-nums",
                        isWinner ? "text-accent-700" : "text-brand-950"
                      )}
                    >
                      {avgScore}
                      <span className="text-xs font-normal text-slate-400 ml-0.5">
                        avg
                      </span>
                    </span>
                  )}
                </div>

                {/* Sentiment Scores */}
                <div className="space-y-2.5">
                  {dimensions.map((dim) => (
                    <ScoreBar
                      key={dim}
                      label={dim}
                      score={r.scores[dim] ?? null}
                      isWinner={isWinner && dim === "recommendation"}
                    />
                  ))}
                </div>

                {/* Key Quote */}
                {r.reasoning && r.reasoning !== "Not mentioned." && (
                  <div className="mt-4 flex items-start gap-2 rounded-md bg-slate-50 p-3">
                    <Quote className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                    <Markdown content={r.reasoning} className="text-[13px] text-slate-600 italic" />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {isBlindQuery && mentions.length > 0 && (
        <Card>
          <CardContent className="py-5 px-6">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquareText className="w-4 h-4 text-brand-600" />
              <p className="text-[13px] font-semibold text-brand-950">Other brands mentioned</p>
            </div>
            <div className="space-y-3">
              {mentions.map(mention => (
                <div key={mention.id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[13px] font-semibold text-brand-950">{mention.brand_name}</span>
                    <span className="text-[11px] text-slate-500">
                      Visibility {mention.visibility ?? "N/A"} · Sentiment {mention.sentiment ?? "N/A"}
                    </span>
                  </div>
                  {mention.context && <p className="mt-1 text-[12px] text-slate-600 leading-relaxed">{mention.context}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
